import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowLeft, FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash, FaSave, FaTimes } from 'react-icons/fa';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-hot-toast';
import DismissableBanner from '../components/DismissableBanner';

const BannerManagement = ({ onBack }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [banners, setBanners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [previewBanner, setPreviewBanner] = useState(null);
  
  const [formData, setFormData] = useState({
    message: '',
    type: 'info',
    customColor: '#3B82F6',
    enabled: true
  });

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email"
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/banners`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch banners');
      }

      const data = await response.json();
      setBanners(data.banners || []);
    } catch (error) {
      console.error('Error fetching banners:', error);
      toast.error('Failed to fetch banners');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateBanner = async () => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email"
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/banners`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to create banner');
      }

      const newBanner = await response.json();
      setBanners(prev => [newBanner, ...prev]);
      setShowCreateForm(false);
      resetForm();
      toast.success('Banner created successfully!');
    } catch (error) {
      console.error('Error creating banner:', error);
      toast.error('Failed to create banner');
    }
  };

  const handleUpdateBanner = async (bannerId, updates) => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email"
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/banners/${bannerId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Banner update failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(`Failed to update banner: ${response.status} - ${errorData}`);
      }

      const updatedBanner = await response.json();
      setBanners(prev => prev.map(banner => 
        banner.id === bannerId ? updatedBanner : banner
      ));
      setEditingBanner(null);
      resetForm();
      toast.success('Banner updated successfully!');
    } catch (error) {
      console.error('Error updating banner:', error);
      toast.error('Failed to update banner');
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) {
      return;
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email"
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/banners/${bannerId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete banner');
      }

      setBanners(prev => prev.filter(banner => banner.id !== bannerId));
      toast.success('Banner deleted successfully!');
    } catch (error) {
      console.error('Error deleting banner:', error);
      toast.error('Failed to delete banner');
    }
  };

  const handleToggleEnabled = async (bannerId, enabled) => {
    await handleUpdateBanner(bannerId, { enabled });
  };

  const resetForm = () => {
    setFormData({
      message: '',
      type: 'info',
      customColor: '#3B82F6',
      enabled: true
    });
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner.id);
    setFormData({
      message: banner.message,
      type: banner.type,
      customColor: banner.customColor || '#3B82F6',
      enabled: banner.enabled
    });
  };

  const handleCancelEdit = () => {
    setEditingBanner(null);
    setShowCreateForm(false);
    resetForm();
  };

  const handlePreview = () => {
    const previewData = {
      id: 'preview',
      ...formData,
      enabled: true
    };
    setPreviewBanner(previewData);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'info': return 'text-blue-600 bg-blue-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      case 'error': return 'text-red-600 bg-red-50';
      case 'success': return 'text-green-600 bg-green-50';
      case 'custom': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const renderBannerForm = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08] mb-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-['VT323'] text-xl text-black/60">
          {editingBanner ? 'Edit Banner' : 'Create New Banner'}
        </h3>
        <div className="flex gap-2">
          <motion.button
            onClick={handlePreview}
            className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 font-['VT323'] text-[16px] rounded-lg transition-colors flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaEye className="w-4 h-4" />
            Preview
          </motion.button>
          <motion.button
            onClick={handleCancelEdit}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-['VT323'] text-[16px] rounded-lg transition-colors flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaTimes className="w-4 h-4" />
            Cancel
          </motion.button>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-['VT323'] text-black/60 mb-2">
            Banner Message
          </label>
          <textarea
            value={formData.message}
            onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
            placeholder="Enter your banner message..."
            className="w-full px-4 py-3 bg-white/90 text-black border border-black/10 rounded-xl font-['VT323'] text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:bg-white hover:bg-white transition-all duration-200 min-h-[80px] resize-y"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-['VT323'] text-black/60 mb-2">
              Banner Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-4 py-3 bg-white/90 text-black border border-black/10 rounded-xl font-['VT323'] text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:bg-white hover:bg-white transition-all duration-200"
            >
              <option value="info">Info (Blue)</option>
              <option value="success">Success (Green)</option>
              <option value="warning">Warning (Yellow)</option>
              <option value="error">Error (Red)</option>
              <option value="custom">Custom Color</option>
            </select>
          </div>

          {formData.type === 'custom' && (
            <div>
              <label className="block text-sm font-['VT323'] text-black/60 mb-2">
                Custom Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={formData.customColor}
                  onChange={(e) => setFormData(prev => ({ ...prev, customColor: e.target.value }))}
                  className="w-12 h-12 rounded-lg border border-black/10 cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.customColor}
                  onChange={(e) => setFormData(prev => ({ ...prev, customColor: e.target.value }))}
                  className="flex-1 px-4 py-3 bg-white/90 text-black border border-black/10 rounded-xl font-['VT323'] text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  placeholder="#3B82F6"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="enabled"
            checked={formData.enabled}
            onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
          />
          <label htmlFor="enabled" className="font-['VT323'] text-[16px] text-black/60">
            Enable banner immediately
          </label>
        </div>

        <div className="flex justify-end gap-3">
          <motion.button
            onClick={editingBanner ? () => handleUpdateBanner(editingBanner, formData) : handleCreateBanner}
            disabled={!formData.message.trim()}
            className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-['VT323'] text-[18px] rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaSave className="w-4 h-4" />
            {editingBanner ? 'Update Banner' : 'Create Banner'}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <motion.div 
          className="w-8 h-8 border-2 border-black/20 border-t-black/40 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-[920px] mx-auto">
      {/* Preview Banner */}
      <AnimatePresence>
        {previewBanner && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="relative"
          >
            <div className="absolute -top-2 left-4 bg-yellow-400 text-black px-2 py-1 rounded text-xs font-['VT323'] z-10">
              PREVIEW
            </div>
            <DismissableBanner 
              banner={previewBanner} 
              onDismiss={() => setPreviewBanner(null)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <motion.button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[18px] text-black/60 hover:text-black/80 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaArrowLeft className="w-4 h-4" />
            Back to Admin
          </motion.button>
          <h1 className="text-2xl font-['VT323'] text-black/60">Banner Management</h1>
        </div>
        
        {!showCreateForm && !editingBanner && (
          <motion.button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-['VT323'] text-[18px] rounded-lg transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaPlus className="w-4 h-4" />
            Create Banner
          </motion.button>
        )}
      </div>

      {/* Create/Edit Form */}
      {(showCreateForm || editingBanner) && renderBannerForm()}

      {/* Banners List */}
      <div className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08]">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-['VT323'] text-xl text-black/40">Active Banners</h2>
          <div className="text-sm font-['VT323'] text-black/40">
            {banners.length} banner{banners.length !== 1 ? 's' : ''}
          </div>
        </div>

        {banners.length === 0 ? (
          <div className="text-center py-12">
            <p className="font-['VT323'] text-[18px] text-black/40 mb-4">
              No banners created yet
            </p>
            <motion.button
              onClick={() => setShowCreateForm(true)}
              className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-['VT323'] text-[18px] rounded-xl transition-colors flex items-center gap-2 mx-auto"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FaPlus className="w-4 h-4" />
              Create Your First Banner
            </motion.button>
          </div>
        ) : (
          <div className="space-y-4">
            {banners.map(banner => (
              <motion.div
                key={banner.id}
                className="p-4 bg-black/[0.02] hover:bg-black/[0.04] rounded-lg border border-black/[0.05] transition-colors"
                whileHover={{ scale: 1.01 }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 mr-4">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2 py-1 text-xs font-['VT323'] rounded-full ${getTypeColor(banner.type)}`}>
                        {banner.type.toUpperCase()}
                      </span>
                      <span className={`px-2 py-1 text-xs font-['VT323'] rounded-full ${
                        banner.enabled ? 'text-green-600 bg-green-50' : 'text-gray-600 bg-gray-50'
                      }`}>
                        {banner.enabled ? 'ENABLED' : 'DISABLED'}
                      </span>
                      {banner.type === 'custom' && (
                        <div 
                          className="w-4 h-4 rounded-full border border-black/20"
                          style={{ backgroundColor: banner.customColor }}
                        />
                      )}
                    </div>
                    <p className="font-['VT323'] text-[16px] text-black/70 mb-2">
                      {banner.message}
                    </p>
                    <p className="text-xs font-['VT323'] text-black/40">
                      Created {new Date(banner.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <motion.button
                      onClick={() => handleToggleEnabled(banner.id, !banner.enabled)}
                      className={`p-2 rounded-lg transition-colors ${
                        banner.enabled 
                          ? 'bg-green-100 hover:bg-green-200 text-green-700' 
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      title={banner.enabled ? 'Disable banner' : 'Enable banner'}
                    >
                      {banner.enabled ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
                    </motion.button>
                    
                    <motion.button
                      onClick={() => handleEdit(banner)}
                      className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      title="Edit banner"
                    >
                      <FaEdit className="w-4 h-4" />
                    </motion.button>
                    
                    <motion.button
                      onClick={() => handleDeleteBanner(banner.id)}
                      className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      title="Delete banner"
                    >
                      <FaTrash className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BannerManagement;
